/**
 * ViewPreInvoiceDropdownActions - Componente de acciones para prefacturas en vista de detalle
 *
 * Dropdown con acciones disponibles para cada prefactura:
 * - Editar: Editar la prefactura
 * - Devolver a Pedidos: Devolver la prefactura al estado de pedidos
 * - Bloquear: Bloquear la prefactura
 */

import { faPen, faReply, faBan, faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useInvoicesPermissions } from '@modules/Invoices/InvoicesOrders/List/hooks/useInvoicesPermissions';

interface ViewPreInvoiceDropdownActionsProps {
  preInvoiceId: number;
  onAction: (action: 'edit' | 'return' | 'block', preInvoiceId: number) => void;
}

export const ViewPreInvoiceDropdownActions = ({ preInvoiceId, onAction }: ViewPreInvoiceDropdownActionsProps) => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { canEditLines, canRestoreLines, canBlockLines } = useInvoicesPermissions();

  const actions = [
    {
      id: 'edit',
      label: t('actions.edit', 'Editar'),
      icon: faPen,
      onClick: () => onAction('edit', preInvoiceId),
      canShow: canEditLines,
      testId: 'edit-preinvoice-action',
    },
    {
      id: 'return',
      label: t('actions.return', 'Devolver a Pedidos'),
      icon: faReply,
      onClick: () => onAction('return', preInvoiceId),
      canShow: canRestoreLines,
      testId: 'return-preinvoice-action',
    },
    {
      id: 'block',
      label: t('actions.block', 'Bloquear'),
      icon: faBan,
      onClick: () => onAction('block', preInvoiceId),
      canShow: canBlockLines,
      testId: 'block-preinvoice-action',
    },
  ].filter((action) => action.canShow);

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`preinvoice-actions-${preInvoiceId}`}>
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {actions.map((action) => (
          <DropdownMenuItem key={action.id} onClick={action.onClick} data-testid={action.testId}>
            <FontAwesomeIcon icon={action.icon} className="mr-2 h-3.5 w-3.5 text-icons" />
            <span>{action.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
